
import { useState, useEffect } from "react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Mail, Phone, X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import CanonicalTag from "@/components/CanonicalTag";
import { NeumorphicThemeProvider, useNeumorphicTheme } from "@/contexts/NeumorphicThemeContext";
import NeumorphicThemeToggle from "@/components/NeumorphicThemeToggle";
import { NeumorphicContainer, NeumorphicCard } from "@/components/neumorphic";

const ContactUsContent = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const { theme } = useNeumorphicTheme();

  useEffect(() => {
    // Add ContactPage structured data
    const contactSchema = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact UDA ADU",
      "description": "Schedule a free consultation or ask questions about building your ADU.",
      "url": "https://www.udaadu.com/contact-us"
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(contactSchema);
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      const existingJsonLd = document.querySelector('script[type="application/ld+json"]');
      if (existingJsonLd && existingJsonLd.textContent?.includes('"url": "https://www.udaadu.com/contact-us"')) {
        existingJsonLd.remove();
      }
    };
  }, []);

  const openCalendlyModal = () => {
    console.log('Opening Calendly modal');
    setShowCalendly(true);
  };

  const closeCalendlyModal = () => {
    console.log('Closing Calendly modal');
    setShowCalendly(false);
  };

  const textColor = theme === "dark" ? "text-white" : "text-[#1a1a1a]";
  const mutedTextColor = theme === "dark" ? "text-gray-300" : "text-[#4a4a4a]";

  return (
    <NeumorphicContainer>
      <CanonicalTag url="https://www.udaadu.com/contact-us" />
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <NeumorphicCard className="relative w-full max-w-4xl h-[90vh] overflow-hidden p-2">
            <button 
              onClick={closeCalendlyModal}
              className={`absolute top-4 right-4 z-10 p-2 rounded-full shadow-md transition-colors ${
                theme === "dark" 
                  ? "bg-[#0a0a0a] hover:bg-[#141414] text-white" 
                  : "bg-[#f5f5f5] hover:bg-[#e8e8e8] text-[#1a1a1a]"
              }`}
            >
              <X className="h-6 w-6" />
            </button>
            <iframe
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1"
              width="100%"
              height="100%"
              frameBorder="0"
              title="Schedule a meeting"
              className="rounded-xl"
            />
          </NeumorphicCard>
        </div>
      )}

      {/* Main Content - Adjusted positioning */}
      <div className="px-4 pt-20 md:pt-24 pb-12">
        <div className="max-w-md w-full mx-auto">
          {/* Get in Touch Card */}
          <NeumorphicCard className="p-8 mb-8">
            <h1 className={`text-2xl md:text-3xl font-bold text-center mb-8 ${textColor}`}>
              Get in Touch
            </h1>
            
            <div className="space-y-6">
              {/* Email */}
              <div className="flex items-start gap-4">
                <Mail className={`h-6 w-6 mt-1 flex-shrink-0 ${textColor}`} />
                <div>
                  <h3 className={`font-medium mb-1 ${textColor}`}>Email</h3>
                  <p className={mutedTextColor}>info@udaadu.com</p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-4">
                <Phone className={`h-6 w-6 mt-1 flex-shrink-0 ${textColor}`} />
                <div>
                  <h3 className={`font-medium mb-1 ${textColor}`}>Phone</h3>
                  <p className={mutedTextColor}>+1 (408) 912-8989</p>
                </div>
              </div>
            </div>
          </NeumorphicCard>

          {/* Book Meeting Button */}
          <div className="text-center">
            <Button 
              onClick={openCalendlyModal}
              className={`px-8 py-3 text-lg font-semibold rounded-xl transition-colors w-full border-0 ${
                theme === "dark"
                  ? "bg-[#0a0a0a] hover:bg-[#141414] text-white shadow-[6px_6px_12px_#000000,-6px_-6px_12px_#141414]"
                  : "bg-white hover:bg-gray-100 text-[#1a1a1a] shadow-[6px_6px_12px_#c8c8c8,-6px_-6px_12px_#ffffff]"
              }`}
            >
              Book your complementary meeting
            </Button>
          </div>
        </div>
      </div>

      <Footer />
      <FloatingADUWizard />
      <NeumorphicThemeToggle />
    </NeumorphicContainer>
  );
};

const ContactUs = () => {
  return (
    <NeumorphicThemeProvider>
      <ContactUsContent />
    </NeumorphicThemeProvider>
  );
};

export default ContactUs;
